import { call, put, takeLatest, takeEvery, take, select } from 'redux-saga/effects'
import type { PayloadAction } from '@reduxjs/toolkit'
import { collection, query, where, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, writeBatch, Timestamp } from 'firebase/firestore'
import { showToast } from '@/components/app/Toast/showToast'
import { createSnapshotChannel } from '../utils/createSnapshotChannel'
import { history } from '../../lib/history'
import { db } from '../../lib/firebase'
import {
    createListSaga,
    updateListSaga,
    deleteListSaga,
    transferListSaga,
    setLists,
    addListLocal,
    updateListLocal,
    removeListLocal,
    setScrollToListId,
    setLoading,
    setError,
} from './listsSlice'
import { selectAllLists } from './listsSelectors'
import { selectAuth } from '../auth/authSelectors'
import { selectTasksById } from '../tasks/tasksSelectors'
import { selectProjectById } from '../projects/projectsSelectors'
import { selectWorkspace } from '../workspace/workspaceSelectors'
import type { List } from '../../contexts/ListsContext'
import type { RootState } from '../index'
import { logTiming } from '@/utils/timing'

function* createListWorker(action: PayloadAction<{ projectId: string; name: string; workspaceId: string; afterListId?: string }>): Generator<any, void, any> {
    const { projectId, name, workspaceId, afterListId } = action.payload

    try {
        const lists: List[] = yield select(selectAllLists)
        const projectLists = lists
            .filter((l: List) => l.projectId === projectId)
            .sort((a: List, b: List) => a.order - b.order)

        // Default: append to the end of this project. When created from a section's menu
        // (afterListId set), insert right after that section using a fractional order between it
        // and the next one — no need to renumber the others (SCD-159).
        let order: number
        if (afterListId) {
            const anchorIdx = projectLists.findIndex((l: List) => l.id === afterListId)
            const anchor = projectLists[anchorIdx]
            const next = anchorIdx >= 0 ? projectLists[anchorIdx + 1] : undefined
            if (anchor) {
                order = next ? (anchor.order + next.order) / 2 : anchor.order + 1
            } else {
                order = projectLists.reduce((max: number, l: List) => Math.max(max, l.order), -1) + 1
            }
        } else {
            order = projectLists.reduce((max: number, l: List) => Math.max(max, l.order), -1) + 1
        }

        // Pre-generate the id so we can scroll the new section into view as soon as it renders.
        const ref = doc(collection(db, 'lists'))

        const authState = yield select(selectAuth)
        if (!authState) {
            yield put(addListLocal({
                id: ref.id,
                projectId,
                workspaceId,
                name,
                order,
                createdAt: Timestamp.now(),
            }))
            yield put(setScrollToListId(ref.id))
            showToast('List created')
            return
        }

        yield call(() => setDoc(ref, {
            projectId,
            workspaceId,
            name,
            order,
            createdAt: serverTimestamp()
        }))
        yield put(setScrollToListId(ref.id))
        showToast('List created')
    } catch (err) {
        console.error('Error creating list:', err)
    }
}

function* updateListWorker(action: PayloadAction<{ listId: string; data: Partial<Pick<List, 'name' | 'order'>> }>): Generator<any, void, any> {
    const { listId, data } = action.payload

    const authState = yield select(selectAuth)
    if (!authState) {
        yield put(updateListLocal({ listId, data }))
        return
    }

    try {
        const listRef = doc(db, 'lists', listId)
        yield call(() => updateDoc(listRef, data))
    } catch (err) {
        console.error('Error updating list:', err)
    }
}

function* deleteListWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const listId = action.payload

    const authState = yield select(selectAuth)
    if (!authState) {
        yield put(removeListLocal(listId))
        showToast('List deleted')
        return
    }

    try {
        const listRef = doc(db, 'lists', listId)
        yield call(deleteDoc, listRef)
        showToast('List deleted')
    } catch (err) {
        console.error('Error deleting list:', err)
    }
}

function* transferListWorker(action: PayloadAction<{ listId: string; newProjectId: string }>): Generator<any, void, any> {
    const { listId, newProjectId } = action.payload

    const authState = yield select(selectAuth)
    if (!authState) return

    try {
        // Calculate order: last in target project
        const allLists: List[] = yield select(selectAllLists)
        const targetProjectMaxOrder = allLists
            .filter(l => l.projectId === newProjectId)
            .reduce((max: number, l: List) => Math.max(max, l.order), -1)

        // Update list: new projectId and order
        const listRef = doc(db, 'lists', listId)
        yield call(() => updateDoc(listRef, { projectId: newProjectId, order: targetProjectMaxOrder + 1 }))

        // Update all tasks in this list atomically
        const tasksById = yield select(selectTasksById)
        const taskIds = Object.keys(tasksById).filter(id => tasksById[id].listId === listId)
        if (taskIds.length > 0) {
            const batch = writeBatch(db)
            for (const taskId of taskIds) {
                batch.update(doc(db, 'tasks', taskId), { projectId: newProjectId })
            }
            yield call([batch, batch.commit])
        }
        const project = yield select((state: RootState) => selectProjectById(state, newProjectId))
        const workspace = yield select(selectWorkspace)
        const workspaceUrlId = workspace?.workspaceUrlId
        const projectName = project?.name || 'project'
        showToast(`Moved to ${projectName}`, {
            linkText: 'Open',
            onLinkClick: () => history.push(`/${workspaceUrlId}/projects/${newProjectId}`),
        })
    } catch (err) {
        console.error('Error transferring list:', err)
    }
}

// Exported listener for workspace-level subscription

export function* listenAllLists(workspaceId: string): Generator<any, void, any> {
    logTiming(`[lists] listenAllLists started (workspaceId=${workspaceId})`)
    const channel: ReturnType<typeof createSnapshotChannel> = yield call(
        createSnapshotChannel,
        query(collection(db, 'lists'), where('workspaceId', '==', workspaceId))
    )

    let firstData = true
    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                yield put(setLists(
                    event.snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as List))
                ))
                yield put(setLoading(false))
                if (firstData) {
                    logTiming(`[lists] first snapshot (${event.snapshot.size} docs)`)
                    firstData = false
                }
            } else {
                yield put(setError('Failed to load lists'))
                yield put(setLoading(false))
            }
        }
    } finally {
        channel.close()
    }
}

export function* watchListsSaga() {
    yield takeLatest(createListSaga.type, createListWorker)
    yield takeEvery(updateListSaga.type, updateListWorker)
    yield takeLatest(deleteListSaga.type, deleteListWorker)
    yield takeEvery(transferListSaga.type, transferListWorker)
}
